import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import ScrollToTop from './components/ScrollToTop';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import MobileBookingBar from './components/MobileBookingBar';
import Home from './pages/Home';
import Motorhomes from './pages/Motorhomes';
import MotorhomeDetail from './pages/MotorhomeDetail';
import Servicing from './pages/Servicing';
import Reviews from './pages/Reviews';
import HowItWorks from './pages/HowItWorks';
import Gallery from './pages/Gallery';
import FAQs from './pages/FAQs';
import OnTheDay from './pages/OnTheDay';
import TermsConditions from './pages/TermsConditions';
import GiftVouchers from './pages/GiftVouchers';
import GiftVoucherSuccess from './pages/GiftVoucherSuccess';
import RollerTeamParts from './pages/RollerTeamParts';
import LocationPage from './pages/LocationPage';
import Guides from './pages/Guides';
import GuideDetail from './pages/GuideDetail';

// Admin imports
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminMotorhomes from './pages/admin/AdminMotorhomes';
import AdminReviews from './pages/admin/AdminReviews';
import AdminFAQs from './pages/admin/AdminFAQs';
import AdminServicing from './pages/admin/AdminServicing';
import AdminGallery from './pages/admin/AdminGallery';
import AdminSettings from './pages/admin/AdminSettings';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
};

// Public Layout (with Navbar/Footer)
const PublicLayout = ({ children }) => (
  <div className="pb-[68px] md:pb-0">
    <Navbar />
    {children}
    <Footer />
    <MobileBookingBar />
  </div>
);

function AppContent() {
  return (
    <>
      <ScrollToTop />
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
      <Route path="/motorhomes" element={<PublicLayout><Motorhomes /></PublicLayout>} />
      <Route path="/motorhomes/:slug" element={<PublicLayout><MotorhomeDetail /></PublicLayout>} />
      <Route path="/servicing" element={<PublicLayout><Servicing /></PublicLayout>} />
      <Route path="/reviews" element={<PublicLayout><Reviews /></PublicLayout>} />
      <Route path="/how-it-works" element={<PublicLayout><HowItWorks /></PublicLayout>} />
      <Route path="/gallery" element={<PublicLayout><Gallery /></PublicLayout>} />
      <Route path="/faqs" element={<PublicLayout><FAQs /></PublicLayout>} />
      <Route path="/on-the-day" element={<PublicLayout><OnTheDay /></PublicLayout>} />
      <Route path="/terms-and-conditions" element={<PublicLayout><TermsConditions /></PublicLayout>} />
      <Route path="/gift-vouchers" element={<PublicLayout><GiftVouchers /></PublicLayout>} />
      <Route path="/gift-vouchers/success" element={<PublicLayout><GiftVoucherSuccess /></PublicLayout>} />
      <Route path="/roller-team-parts" element={<PublicLayout><RollerTeamParts /></PublicLayout>} />
      <Route path="/motorhome-hire" element={<Navigate to="/motorhome-hire/suffolk" replace />} />
      <Route path="/motorhome-hire/:slug" element={<PublicLayout><LocationPage /></PublicLayout>} />
      <Route path="/guides" element={<PublicLayout><Guides /></PublicLayout>} />
      <Route path="/guides/:slug" element={<PublicLayout><GuideDetail /></PublicLayout>} />
      <Route path="/terms" element={<Navigate to="/terms-and-conditions" replace />} />
      <Route path="/privacy" element={<Navigate to="/terms-and-conditions" replace />} />

      {/* Admin Routes */}
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/admin/*" element={
        <ProtectedRoute>
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="motorhomes" element={<AdminMotorhomes />} />
        <Route path="reviews" element={<AdminReviews />} />
        <Route path="faqs" element={<AdminFAQs />} />
        <Route path="servicing" element={<AdminServicing />} />
        <Route path="gallery" element={<AdminGallery />} />
        <Route path="settings" element={<AdminSettings />} />
      </Route>
    </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
